import { z } from 'zod';
import {
  EstimateUom,
  SubjectType,
  TaskAssignee,
  TaskKind,
  TaskPriority,
} from './entities/task-module/task.js';
import { IsoDate, PositiveDecimalString, UuidString } from './primitives.js';

/**
 * GQ-1 — единый strict API-контракт создания задачи для FE и BE.
 *
 * Request-схема намеренно находится вне `entities/**`: response entities в проекте
 * forward-compatible, а входной write-контракт, напротив, обязан отвергать unknown fields.
 * Публичный импорт остаётся `@staiter/shared/task-module` через barrel.
 *
 * `project_id` определяет primary-проект; `entity_id` и UI-понятия `groupBy`/`groupKey`
 * отсутствуют. Для клиента экспортируется input-type, поэтому server defaults не
 * становятся обязательными полями request.
 */
export const TaskCreateRequestSchema = z
  .object({
    project_id: UuidString,
    state_id: UuidString.optional(),
    parent_task_id: UuidString.nullable().optional(),
    kind: TaskKind.default('task'),
    title: z.string().min(1).max(500),
    description_json: z.unknown().optional(),
    priority: TaskPriority.default('none'),
    start_date: IsoDate.nullable().optional(),
    due_date: IsoDate.nullable().optional(),
    estimate_value: PositiveDecimalString.nullable().optional(),
    estimate_uom: EstimateUom.nullable().optional(),
    assignees: z.array(TaskAssignee).default([]),
    author_user_id: UuidString.optional(),
    subject_type: SubjectType.nullable().optional(),
    subject_id: UuidString.nullable().optional(),
    custom_fields: z.record(z.string(), z.unknown()).default({}),
    is_draft: z.boolean().default(false),
    position: z.enum(['start', 'end']).default('end'),
    before_task_id: UuidString.optional(),
    after_task_id: UuidString.optional(),
  })
  .strict()
  .refine((d) => (d.subject_type == null) === (d.subject_id == null), {
    message: 'subject_type и subject_id должны быть оба null или оба заданы',
    path: ['subject_id'],
  })
  .refine((d) => (d.estimate_value == null) === (d.estimate_uom == null), {
    message: 'estimate_value и estimate_uom должны быть оба null или оба заданы',
    path: ['estimate_uom'],
  });

export type TaskCreateRequest = z.input<typeof TaskCreateRequestSchema>;
export type TaskCreateValidated = z.output<typeof TaskCreateRequestSchema>;
